import React, { useState, cloneElement } from 'react';
import { Button } from 'reactstrap';
import PropTypes from 'prop-types';
import ConfirmModal from 'common/confirmAlert';
import CButton from './CButton';
import AccessControlWrapper from './PermissionButton';

const ConfirmButton = ({
    children,
    onConfirm,
    message = "Are you sure?",
    disabled = false,
    className = "",
    renderButton,
    contextUri,
    actionType,
    validateBeforeConfirm,
    onModalOpen,
    onModalClose,
    ...buttonProps
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleButtonClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        // If validation function is provided, run it first
        if (validateBeforeConfirm) {
            const isValid = await validateBeforeConfirm();
            if (!isValid) {
                // Don't show confirmation modal if validation fails
                return;
            }
        }

        setIsModalOpen(true);
        if (onModalOpen) onModalOpen();
    };

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm();
        }
        setIsModalOpen(false);
        if (onModalClose) onModalClose();
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        if (onModalClose) onModalClose();
    };

    // If renderButton is provided, use it with the click handler
    const buttonElement = renderButton ?
        cloneElement(renderButton, {
            onClick: handleButtonClick,
            disabled: disabled
        }) :
        (
            <Button
                disabled={disabled}
                className={className}
                onClick={handleButtonClick}
                {...buttonProps}
            >
                {children}
            </Button>
        );

    // Conditionally wrap with AccessControlWrapper if permission props are provided
    const finalButtonElement = (contextUri && actionType) ? (
        <AccessControlWrapper contextUri={contextUri} actionType={actionType}>
            {buttonElement}
        </AccessControlWrapper>
    ) : buttonElement;

    return (
        <>
            {finalButtonElement}

            <ConfirmModal
                isOpen={isModalOpen}
                onClose={handleCancel}
                onConfirm={handleConfirm}
                title="Confirm" 
                message={message}
                confirmText="Confirm"
                cancelText="Cancel"
                confirmColor="primary"
                cancelColor="light"
                size="md"
                centered={true}
            />
        </>
    );
};

ConfirmButton.propTypes = {
    children: PropTypes.node,
    onConfirm: PropTypes.func.isRequired,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    color: PropTypes.string,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    renderButton: PropTypes.element,
    contextUri: PropTypes.string,
    actionType: PropTypes.string,
    validateBeforeConfirm: PropTypes.func,
    onModalOpen: PropTypes.func,
    onModalClose: PropTypes.func
};

export default ConfirmButton;